import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { PageContainer } from "../components/PageContainer";
import { authService, AppUser } from "../services/auth.service";
import { loginSchema, LoginFormData } from "../helpers/formValidation";
import { ErrorMessage } from "../components/form/ErrorMessage";
import { Seo } from "../components/Seo";
import {
  formBtnPrimaryClasses,
  formInputClasses,
  formLabelClasses,
} from "../styles/formClasses";
import {
  authCardClasses,
  authPageShellClasses,
  centeredPageHeaderClasses,
  pageHeadingClasses,
  pageLeadClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import { authPageClasses, loginPageClasses } from "../styles/pageSpecificClasses";

const ADMIN_ROLE = "admin";

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: LoginFormData) => {
    setServerError("");
    setLoading(true);

    try {
      const auth = await authService.loginRequest(data.email, data.password);

      let appUser: AppUser | null = null;
      const me = await authService.fetchMe(auth.accessToken);
      if (me) {
        appUser = {
          id: me.id,
          email: me.email,
          fullName: me.displayName ?? me.email.split("@")[0],
          roles: me.roles ?? [],
        };
      }

      if (!appUser) {
        appUser = authService.buildUserFromJwt(auth.accessToken);
        if (!appUser) {
          throw new Error("Could not read user profile from token.");
        }
      }

      const isAdmin = appUser.roles.some((role) => role.toLowerCase() === ADMIN_ROLE);
      if (!isAdmin) {
        throw new Error("This account does not have admin access.");
      }

      login(auth.accessToken, appUser);
      const nextPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(nextPath ?? "/admin/blog");
    } catch (err: any) {
      setServerError(err?.message ?? "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer maxWidth="full" contentClassName={authPageShellClasses}>
      <Seo
        title="Admin Login"
        description="Private admin sign-in for managing Summer Jobs in Greece editorial content."
        path="/admin-login"
        robots="noindex,nofollow"
      />

      <div className={loginPageClasses.shellWidth}>
        <div className={centeredPageHeaderClasses}>
          <span className={subtleEyebrowClasses}>Admin Access</span>
          <h1 className={`${loginPageClasses.heading} ${pageHeadingClasses}`}>Admin login</h1>
          <p className={pageLeadClasses}>
            Sign in to manage blog content and editorial updates.
          </p>
        </div>

        <div className={`${authCardClasses} ${authPageClasses.authCardSpacing}`}>
          {serverError && (
            <div className={loginPageClasses.error}>
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className={authPageClasses.authForm}>
            <div>
              <label className={formLabelClasses}>Email</label>
              <input
                {...register("email")}
                type="email"
                placeholder="your.email@example.com"
                className={formInputClasses}
                autoComplete="email"
              />
              <ErrorMessage message={errors.email?.message} />
            </div>

            <div>
              <label className={formLabelClasses}>Password</label>
              <input
                {...register("password")}
                type="password"
                placeholder="Enter your password"
                className={formInputClasses}
                autoComplete="current-password"
              />
              <ErrorMessage message={errors.password?.message} />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`${formBtnPrimaryClasses} ${authPageClasses.disabledButton}`}
            >
              {loading ? "Signing in..." : "Log In"}
            </button>
          </form>

          <div className={loginPageClasses.utilityRow}>
            <button className={loginPageClasses.utilityButton}>
              Forgot Password?
            </button>
            <p>Public visitors can browse the blog without an account.</p>
          </div>
        </div>

        <p className={authPageClasses.legalCopy}>
          By continuing, you agree to our <Link to="/terms" className={authPageClasses.legalLink}>Terms</Link> and{" "}
          <Link to="/privacy" className={authPageClasses.legalLink}>Privacy Policy</Link>.
        </p>
      </div>
    </PageContainer>
  );
};

export default Login;
